import api from './api';

export type IcpCampaignStatus =
  | 'draft'
  | 'searched'
  | 'enriching'
  | 'enriched'
  | 'imported'
  | 'ready'
  | 'active';

export interface IcpCampaign {
  id: string;
  name: string;
  description?: string | null;
  status: IcpCampaignStatus;
  icpId?: string | null;
  personaId?: string | null;
  offerId?: string | null;
  leadMagnetId?: string | null;
  sequenceId?: string | null;
  filters?: string | null;
  searchTerm?: string | null;
  lemlistCampaignId?: string | null;
  lemlistSequenceId?: string | null;
  lemlistPushedAt?: string | null;
  tone: string;
  language: string;
  goal?: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { prospects: number };
  prospects?: IcpCampaignProspect[];
}

export interface IcpCampaignProspect {
  id: string;
  campaignId: string;
  lemlistLeadId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  title?: string | null;
  company?: string | null;
  location?: string | null;
  country?: string | null;
  linkedInUrl?: string | null;
  qualityScore?: number | null;
  department?: string | null;
  email?: string | null;
  emailStatus?: string | null;
  phone?: string | null;
  enrichStatus: 'pending' | 'enriching' | 'enriched' | 'failed';
  enrichError?: string | null;
  leadId?: string | null;
  status: 'found' | 'enriching' | 'enriched' | 'failed' | 'imported' | 'icebroken' | 'synced';
  createdAt: string;
  updatedAt: string;
}

export interface ProposedFilter {
  filterId: string;
  in: string[];
  out: string[];
  sourceField?: string;
  note?: string;
}

export interface LemlistFilter {
  filterId: string;
  description?: string;
  mode?: string[];
  type?: string;
  helper?: string;
}

export interface CompanyResult {
  companyId?: string;
  name?: string;
  domain?: string;
  industry?: string;
  location?: string;
  country?: string;
  size?: string;
  linkedInUrl?: string;
}

export interface ProspectStateSummary {
  total: number;
  byStatus: Record<string, number>;
  byEnrichStatus: Record<string, number>;
  enriching: number;
  deliverable: number;
  imported: number;
}

export interface CreateIcpCampaignInput {
  name: string;
  description?: string;
  icpId?: string;
  personaId?: string;
  offerId?: string;
  leadMagnetId?: string;
  sequenceId?: string;
  tone?: string;
  language?: string;
  goal?: string;
}

class IcpCampaignService {
  async list(status?: string): Promise<IcpCampaign[]> {
    const res = await api.get('/icp-campaigns/campaigns', { params: status ? { status } : {} });
    return res.data;
  }

  async get(id: string): Promise<IcpCampaign> {
    const res = await api.get(`/icp-campaigns/campaigns/${id}`);
    return res.data;
  }

  async create(data: CreateIcpCampaignInput): Promise<IcpCampaign> {
    const res = await api.post('/icp-campaigns/campaigns', data);
    return res.data;
  }

  async update(id: string, data: Partial<CreateIcpCampaignInput>): Promise<IcpCampaign> {
    const res = await api.put(`/icp-campaigns/campaigns/${id}`, data);
    return res.data;
  }

  async remove(id: string): Promise<void> {
    await api.delete(`/icp-campaigns/campaigns/${id}`);
  }

  async proposeFilters(id: string): Promise<{ proposals: ProposedFilter[]; availableFilters: LemlistFilter[] }> {
    const res = await api.get(`/icp-campaigns/campaigns/${id}/propose-filters`);
    return res.data;
  }

  async search(
    id: string,
    body: { filters: { filterId: string; in?: string[]; out?: string[] }[]; search?: string; page?: number; size?: number }
  ): Promise<{ created: number; totalAvailable: number; page: number; size: number; limitation?: number }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/search`, body);
    return res.data;
  }

  async proposeCompanyFilters(id: string): Promise<{ proposals: ProposedFilter[]; availableFilters: LemlistFilter[] }> {
    const res = await api.get(`/icp-campaigns/campaigns/${id}/propose-company-filters`);
    return res.data;
  }

  async searchCompanies(
    id: string,
    body: { filters: { filterId: string; in?: string[]; out?: string[] }[]; search?: string; page?: number; size?: number }
  ): Promise<{ companies: CompanyResult[]; totalAvailable: number; page: number; size: number; limitation?: number }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/search-companies`, body);
    return res.data;
  }

  async enrich(id: string, prospectIds?: string[]): Promise<{ started: number; failed: number }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/enrich`, { prospectIds });
    return res.data;
  }

  async pollEnrichments(
    id: string
  ): Promise<{ completed: number; failed: number; remaining: number; summary: ProspectStateSummary }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/poll-enrichments`);
    return res.data;
  }

  async importLeads(id: string): Promise<{ imported: number; skipped: number }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/import-leads`);
    return res.data;
  }

  async generateIcebreakers(id: string): Promise<{ generated: number; failed: number }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/generate-icebreakers`);
    return res.data;
  }

  async push(
    id: string
  ): Promise<{ lemlistCampaignId: string; lemlistSequenceId?: string; synced: number; failed: number }> {
    const res = await api.post(`/icp-campaigns/campaigns/${id}/push`);
    return res.data;
  }

  async getProspects(id: string): Promise<IcpCampaignProspect[]> {
    const res = await api.get(`/icp-campaigns/campaigns/${id}/prospects`);
    return res.data;
  }
}

const icpCampaignService = new IcpCampaignService();
export default icpCampaignService;
